import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout, PageMain } from '../components/WizardShell';
import { ROUTES } from '../utils/createRoutes';
import {
  questionBankOptions,
  getPlannerTopicFilters,
} from '../data/topicPlannerConfig';
import { loadCreateSessionDraft } from '../utils/createSessionFlowStorage';
import type { QuizSlot, TopicQuizConfig } from '../types/topicQuiz';
import {
  canAddBankQuestion,
  difficultyToLevel,
  formatQuizDurationSeconds,
  getQuizFixedQuestionCount,
  getQuizDurationSeconds,
  QUIZ_SECONDS_BY_LEVEL,
  normalizeQuizConfig,
} from '../types/topicQuiz';
import type { DifficultyFilter, TopicFilterValue } from '../data/topicPlannerConfig';
import { loadPlannerTopics, savePlannerTopics } from '../utils/topicPlannerStorage';

const difficultyChips: { id: DifficultyFilter; label: string }[] = [
  { id: 'all', label: 'All Levels' },
  { id: 'Easy', label: 'Easy' },
  { id: 'Medium', label: 'Medium' },
  { id: 'Hard', label: 'Hard' },
];

export function QuestionBankPicker() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topicId = searchParams.get('topicId') ?? '';
  const slot: QuizSlot = searchParams.get('slot') === 'post' ? 'post' : 'pre';

  const topics = loadPlannerTopics();
  const topic = topics.find((t) => t.id === topicId);
  const topicFilters = getPlannerTopicFilters(topics);

  const existingQuiz =
    slot === 'pre' ? topic?.preQuiz : topic?.postQuiz;
  const initialSelected =
    existingQuiz?.mode === 'bank' ? (existingQuiz.selectedQuestionIds ?? []) : [];

  const [searchQuery, setSearchQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState<TopicFilterValue>(
    topic?.index ?? 'all',
  );
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelected);

  const filteredQuestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return questionBankOptions.filter((question) => {
      const matchesTopic =
        topicFilter === 'all' || question.topicIndex === topicFilter;
      const matchesDifficulty =
        difficultyFilter === 'all' || question.difficulty === difficultyFilter;
      const matchesSearch =
        !q ||
        question.text.toLowerCase().includes(q) ||
        String(question.topicIndex).includes(q);
      return matchesTopic && matchesDifficulty && matchesSearch;
    });
  }, [searchQuery, topicFilter, difficultyFilter]);

  const toggleQuestion = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (!canAddBankQuestion(prev, id, slot, questionBankOptions)) return prev;
      return [...prev, id];
    });
  };

  const draftQuiz: TopicQuizConfig = {
    slot,
    mode: 'bank',
    selectedQuestionIds: selectedIds,
  };
  const quizSeconds = getQuizDurationSeconds(draftQuiz, questionBankOptions);
  const targetCount = getQuizFixedQuestionCount(slot);
  const countComplete = selectedIds.length === targetCount;

  const returnToPlanner = () => {
    navigate(ROUTES.topicPlanner, {
      replace: true,
      state: { fromQuestionBank: true, topicId, slot },
    });
  };

  const handleApply = () => {
    if (!topic) {
      returnToPlanner();
      return;
    }
    if (selectedIds.length === 0) return;

    const quiz = normalizeQuizConfig(
      {
        slot,
        mode: 'bank',
        selectedQuestionIds: selectedIds,
      },
      questionBankOptions,
    );

    const next = topics.map((t) => {
      if (t.id !== topicId) return t;
      return slot === 'pre' ? { ...t, preQuiz: quiz } : { ...t, postQuiz: quiz };
    });
    savePlannerTopics(next);
    returnToPlanner();
  };

  const slotLabel = slot === 'pre' ? 'Pre-Quiz' : 'Post-Quiz';

  if (!topic) {
    return (
      <AppLayout>
        <PageMain>
          <div className="alert alert-warning" role="alert">
            Topic not found. Return to the planner and try again.
          </div>
          <Link to={ROUTES.topicPlanner} className="btn btn-outline-secondary">
            Back to Topic Planner
          </Link>
        </PageMain>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageMain>
        <p className="text-muted small mb-3">
          Choose questions for{' '}
          {loadCreateSessionDraft()?.autoSessionName || topic.title}
          {questionBankOptions.length === 0 ? ' (no question bank loaded yet)' : ''}
          {' · '}
          {slotLabel} · Topic {String(topic.index).padStart(2, '0')}: {topic.title}
          {' · '}
          Select exactly {targetCount} questions.
        </p>

        <div className="mb-3">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search questions by keyword..."
            className="form-control"
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Filter by topic</label>
          <div className="d-flex flex-wrap gap-2">
            {topicFilters.map((chip) => (
              <button
                key={String(chip.id)}
                type="button"
                onClick={() => setTopicFilter(chip.id)}
                className={`btn btn-sm ${
                  topicFilter === chip.id ? 'btn-primary' : 'btn-outline-primary'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Filter by difficulty</label>
          <div className="d-flex flex-wrap gap-2">
            {difficultyChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => setDifficultyFilter(chip.id)}
                className={`btn btn-sm ${
                  difficultyFilter === chip.id ? 'btn-dark' : 'btn-outline-secondary'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-muted small mb-3">
          {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} found
        </p>

        <div className="d-flex flex-column gap-2 mb-4">
          {filteredQuestions.length === 0 ? (
            <div className="alert alert-secondary mb-0">No questions match your search or filters.</div>
          ) : (
            filteredQuestions.map((question) => {
              const checked = selectedIds.includes(question.id);
              const atMax =
                !checked && !canAddBankQuestion(selectedIds, question.id, slot, questionBankOptions);
              const level = difficultyToLevel(question.difficulty);
              const topicLabel = topics.find((t) => t.index === question.topicIndex)?.title;
              return (
                <label
                  key={question.id}
                  className={`card ${atMax ? 'opacity-50' : ''}`}
                  style={{ cursor: atMax ? 'not-allowed' : 'pointer' }}
                >
                  <div className="card-body d-flex gap-3 py-3">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={checked}
                      disabled={atMax}
                      onChange={() => toggleQuestion(question.id)}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex flex-wrap gap-2 mb-1">
                        <span className="badge text-bg-primary">
                          Topic {String(question.topicIndex).padStart(2, '0')}
                        </span>
                        {topicLabel && <span className="small text-muted">{topicLabel}</span>}
                        <span className="badge text-bg-light text-dark">
                          L{level} · {QUIZ_SECONDS_BY_LEVEL[level]}s
                        </span>
                      </div>
                      <p className="mb-0 small">{question.text}</p>
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div className="card sticky-bottom mb-0">
          <div className="card-body d-flex flex-column flex-sm-row align-items-sm-center justify-content-end gap-3 py-3">
            <div className="me-sm-auto">
              <div className="fw-semibold">
                {selectedIds.length}/{targetCount} questions selected
              </div>
              <div className={`small ${countComplete ? 'text-success' : 'text-warning'}`}>
                {countComplete
                  ? `Ready to apply · ~${formatQuizDurationSeconds(quizSeconds)} quiz time`
                  : `Select ${targetCount - selectedIds.length} more question${targetCount - selectedIds.length !== 1 ? 's' : ''}`}
              </div>
            </div>
            <div className="codelive-wizard-actions border-0 m-0 p-0">
              <button type="button" onClick={returnToPlanner} className="btn btn-outline-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!countComplete}
                className="btn btn-primary"
              >
                Apply to {slotLabel}
              </button>
            </div>
          </div>
        </div>
      </PageMain>
    </AppLayout>
  );
}
